/**
 * HISTORIALES ACOTADOS (FASE 9.1-B)
 *
 * Hallazgo de UAT: los historiales completos meten demasiado ruido. Por
 * defecto se piden sólo los 10 eventos más recientes (`?limite=10`); «Mostrar
 * más» pide 20 más y «Ver historial completo» los trae todos (tope de
 * servidor: 500 por petición). Sólo cambia lo que se PRESENTA: la base
 * conserva el historial entero, append-only, y nada se resume ni se borra.
 */
import { useState, type ReactNode } from 'react';

import { api } from '../api.ts';
import { useDatos } from './base.tsx';

export const HISTORIAL_INICIAL = 10;
const PASO = 20;
const MAXIMO = 500;

export interface Historial<T> {
  readonly datos: { filas: T[]; total?: number } | null;
  readonly cargando: boolean;
  readonly error: unknown;
  readonly recargar: () => void;
  readonly limite: number;
  readonly mostrarMas: () => void;
  readonly verCompleto: () => void;
}

export function useHistorial<T>(ruta: string): Historial<T> {
  const [limite, setLimite] = useState(HISTORIAL_INICIAL);
  const sep = ruta.includes('?') ? '&' : '?';
  const estado = useDatos(() => api.get<{ filas: T[]; total?: number }>(`${ruta}${sep}limite=${String(limite)}`), [ruta, limite]);
  return {
    ...estado, limite,
    mostrarMas: () => setLimite((l) => Math.min(MAXIMO, l + PASO)),
    verCompleto: () => setLimite(MAXIMO),
  };
}

/** «Mostrando 10 de 37 eventos · Mostrar más · Ver historial completo». Sin `total` (servidor antiguo o mock) no dice nada. */
export function PieHistorial({ historial: h }: { historial: Historial<unknown> }): ReactNode {
  const total = h.datos?.total;
  const mostrados = h.datos?.filas.length ?? 0;
  if (total === undefined || total <= mostrados) return null;
  return (
    <div className="pie-historial fila" role="group" aria-label="Más historial">
      <span className="suave pequena">Mostrando los {mostrados} más recientes de {total} eventos.</span>
      {h.limite < MAXIMO && (
        <>
          <button type="button" className="pequeno" onClick={h.mostrarMas}>Mostrar más</button>
          <button type="button" className="pequeno" onClick={h.verCompleto}>Ver historial completo</button>
        </>
      )}
      {h.limite >= MAXIMO && <span className="suave pequena">Se muestran los {MAXIMO} más recientes; el historial completo se conserva.</span>}
    </div>
  );
}
